import moment from "moment"
import "moment/locale/fr"
import { capitalize } from "../constants/summerContent"
import { formatEuros } from "../../brevet/utils/formatEuros"

const monthLabel = (m) => capitalize(m.locale("fr").format("MMMM"))

/**
 * Le mois plein facturé APRÈS le mois d'entrée d'une date choisie, ou null s'il
 * n'y en a pas. Règle unique de la « 2ᵉ échéance » cours d'été : le mois suivant
 * l'entrée n'existe que s'il est un mois d'édition retenu (août plein après une
 * entrée juillet ; jamais septembre après une entrée août, jamais pour un lead
 * juillet-seul).
 *
 * Dérivé de l'option CHOISIE, pas d'une valeur d'API figée au fetch : reste juste
 * quand le parent choisit une date d'un autre mois sans re-requête. Le récap du
 * picker (via {@link summerMonthlyBreakdown}) ET le hero du checkout s'appuient
 * dessus — une seule règle.
 *
 * @param {object|null} option        option start-options
 * @param {Array<number>} chosenMonths mois d'édition retenus (ex. [7, 8])
 * @returns {import("moment").Moment|null} 1ᵉʳ du mois suivant, ou null
 */
export const summerSecondFullMonth = (option, chosenMonths) => {
    if (!option) return null
    const next = moment(option.first_session || option.start_date)
        .add(1, "month")
        .startOf("month")
    return (chosenMonths || []).includes(next.month() + 1) ? next : null
}

/**
 * Ce que la famille paie PAR MOIS pour une date de démarrage choisie : le mois
 * d'entrée à son prorata (première facture, séances restantes) puis — seulement
 * s'il est retenu — le mois suivant au tarif plein.
 *
 * Le 2ᵉ mois suit {@link summerSecondFullMonth} : il n'existe que si le mois
 * APRÈS l'entrée est un mois d'édition choisi (jamais septembre, jamais pour un
 * lead juillet-seul). Le récap du picker, le bloc prix du compositeur et la 2ᵉ
 * échéance du checkout s'appuient tous sur ce helper — aucune règle de
 * proratisation recalculée ailleurs.
 *
 * Pure ; montants en centimes. Retourne [] sans option.
 *
 * @param {object|null} option            option start-options ({ start_date, first_session, first_charge_amount_cents })
 * @param {number|null} monthlyPriceCents tarif plein d'un mois (variante d'engagement courante)
 * @param {Array<number>} chosenMonths    mois d'édition retenus (ex. [7, 8])
 * @returns {Array<{ label: string, cents: number }>}
 */
export const summerMonthlyBreakdown = (option, monthlyPriceCents, chosenMonths) => {
    if (!option) return []
    const entry = moment(option.first_session || option.start_date)
    const lines = [
        { label: monthLabel(entry), cents: option.first_charge_amount_cents || 0 },
    ]
    const next = summerSecondFullMonth(option, chosenMonths)
    if (next && monthlyPriceCents != null) {
        lines.push({ label: monthLabel(next), cents: monthlyPriceCents })
    }
    return lines
}

/**
 * Phrase « ce que vous payez par mois » pour le bloc prix du compositeur :
 *  - un seul mois           → « 46 € pour août »
 *  - deux mois identiques   → « 139 € par mois »
 *  - deux mois différents   → « 35 € pour juillet et 139 € pour août »
 *
 * @param {Array<{ label: string, cents: number }>} breakdown  cf. summerMonthlyBreakdown
 * @returns {string|null}
 */
export const summerPerMonthSentence = (breakdown) => {
    if (!breakdown?.length) return null
    const euros = (cents) => formatEuros(cents / 100)
    const forMonth = (line) => `${euros(line.cents)} pour ${line.label.toLowerCase()}`

    if (breakdown.length === 1) return forMonth(breakdown[0])
    const [first, second] = breakdown
    if (first.cents === second.cents) return `${euros(first.cents)} par mois`
    return `${forMonth(first)} et ${forMonth(second)}`
}
